import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FolderTree } from 'lucide-react'
import { useMenuManagementStore } from '../../core/stores/menuManagementStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import Modal from '../../components/ui/Modal'
import Input from '../../components/ui/Input'

type CreateCategoryFormData = {
  name: string
  sortOrder?: number
  isVisible?: boolean
}

interface CreateCategoryModalProps {
  businessId: number
  isOpen: boolean
  onClose: () => void
}

export default function CreateCategoryModal({ businessId, isOpen, onClose }: CreateCategoryModalProps) {
  const { t } = useTranslation()
  const { createCategory, isLoading, categories } = useMenuManagementStore()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createCategorySchema = z.object({
    name: z.string().min(1, t('categoryValidationNameRequired')).max(100, t('categoryValidationNameMax')),
    sortOrder: z.number().min(0, t('categoryValidationSortOrderPositive')).optional(),
    isVisible: z.boolean().optional()
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CreateCategoryFormData>({
    resolver: zodResolver(createCategorySchema),
    defaultValues: {
      sortOrder: categories.length + 1,
      isVisible: true
    }
  })

  const onSubmit = async (data: CreateCategoryFormData) => {
    try {
      setIsSubmitting(true)
      await createCategory({
        businessId,
        name: data.name,
        sortOrder: data.sortOrder || categories.length + 1,
        isActive: data.isVisible ?? true
      })
      reset()
      onClose()
    } catch (error) {
      // Error is handled in the store
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-blue-600" />
          <span>{t('addNewCategory')}</span>
        </div>
      }
      actionButtonText={t('save')}
      onAction={handleSubmit(onSubmit)}
      actionButtonLoading={isSubmitting || isLoading}
      actionButtonDisabled={isSubmitting || isLoading}
      actionButtonLoadingText={t('saving')}
      actionButtonClassName="text-sm h-10 py-2 px-4"
      showCancelButton={true}
      cancelButtonText={t('cancel')}
      cancelButtonClassName="border-gray-300 text-[#1E293B] hover:text-[#1E293B] hover:border-[#1E293B] hover:bg-gray-50 text-sm h-10 py-2 px-4"
    >
      <form className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('categoryNameLabel')} *
          </label>
          <Input
            id="name"
            {...register('name')}
            placeholder={t('categoryNameLabel')}
            error={errors.name?.message}
          />
        </div>

        <div>
          <label htmlFor="sortOrder" className="block text-sm font-medium text-gray-700 mb-1">
            {t('categorySortOrderLabel')}
          </label>
          <Input
            id="sortOrder"
            type="number"
            min="1"
            {...register('sortOrder', { valueAsNumber: true })}
            placeholder="1"
            error={errors.sortOrder?.message}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('categorySortOrderHelper')}
          </p>
        </div>

        <div className="flex items-center">
          <input
            id="isVisible"
            type="checkbox"
            {...register('isVisible')}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            defaultChecked
          />
          <label htmlFor="isVisible" className="mr-2 block text-sm text-gray-900">
            {t('categoryActiveLabel')}
          </label>
        </div>
      </form>
    </Modal>
  )
}